import type {
  Activiteit, AiSoort, AiStand, BordMeta, Bestand, BestandInhoud, Bijdrager, Commit,
  CommitBestand, Gesprek, GithubStand, Ik, Issue, Mens, Onderdeel, Project, PullRequest,
  Reactie, Release, Repo, RepoDetail, Sprint, Tak, Taak, Workflowrun,
} from '../../gedeeld/types'

/* ===========================================================================
 *  Praten met de eigen server
 *
 *  Alles loopt via de Worker op hetzelfde adres; de browser heeft nergens
 *  anders een sleutel voor. Wat hij heeft is een koekje, en dat gaat vanzelf
 *  mee.
 *
 *  Waarom een fout hier een Fout is en geen null
 *  ---------------------------------------------
 *
 *  Bij de academie kon een mislukte opslag stil blijven: de les gaat door.
 *  Hier niet. "De taak is aangemaakt" zeggen terwijl hij nooit aankwam, is
 *  de fout die het meest kost -- iemand denkt dat het werk is uitgezet. Elke
 *  aanroep gooit dus bij een mislukking, met de tekst van de server erbij,
 *  en het scherm laat die zien.
 * =========================================================================== */

export class Fout extends Error {
  constructor(public status: number, bericht: string) { super(bericht) }
}

/** Gaat af als de sessie verloopt; de app toont dan het inlogscherm. */
let bijUitgelogd: (() => void) | null = null
export function opUitgelogd(f: () => void) { bijUitgelogd = f }

async function lees<T>(a: Response): Promise<T> {
  let lijf: { ok?: boolean; fout?: string } & Record<string, unknown> = {}
  try { lijf = await a.json() } catch { /* geen JSON: dan volgt hieronder de status */ }
  if (a.status === 401) {
    bijUitgelogd?.()
    throw new Fout(401, 'Je bent uitgelogd.')
  }
  if (!a.ok || lijf.ok === false) {
    throw new Fout(a.status, lijf.fout ?? `De server gaf ${a.status}.`)
  }
  return lijf as T
}

/** Een actie van de functie projects (zie worker/gegevens.ts voor de lijst). */
export async function p<T = Record<string, unknown>>(actie: string, lijf: Record<string, unknown> = {}): Promise<T> {
  let a: Response
  try {
    a = await fetch('/api/p', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...lijf, actie }),
    })
  } catch {
    throw new Fout(0, 'Geen verbinding. Controleer je internet en probeer het opnieuw.')
  }
  return lees<T>(a)
}

async function gh<T>(pad: string, opties?: { methode?: string; lijf?: unknown }): Promise<T> {
  let a: Response
  try {
    a = await fetch('/api/github/' + pad, {
      method: opties?.methode ?? 'GET',
      headers: opties?.lijf ? { 'Content-Type': 'application/json' } : { Accept: 'application/json' },
      body: opties?.lijf ? JSON.stringify(opties.lijf) : undefined,
    })
  } catch {
    throw new Fout(0, 'Geen verbinding met GitHub via de server.')
  }
  return lees<T>(a)
}

/* ------------------------------------------------------------------ *
 *  Wie ben ik
 * ------------------------------------------------------------------ */

export interface Sessie {
  ik: Ik
  verlooptOp: number
  github: { ingesteld: boolean; org: string }
}

/** Null als er niemand is ingelogd. Gooit niet: dit is de eerste vraag. */
export async function haalIk(): Promise<Sessie | null> {
  try {
    const a = await fetch('/api/ik', { headers: { Accept: 'application/json' } })
    if (!a.ok) return null
    const lijf = await a.json() as { ok?: boolean } & Sessie
    return lijf.ok ? lijf : null
  } catch {
    return null
  }
}

export async function uitloggen(): Promise<void> {
  try { await fetch('/api/uitloggen', { method: 'POST' }) } catch { /* vervalt vanzelf */ }
}

/* ------------------------------------------------------------------ *
 *  Projecten, taken, sprints
 * ------------------------------------------------------------------ */

export const api = {
  mensen: () => p<{ mensen: Mens[] }>('mensen').then((x) => x.mensen),

  projecten: (alles = false) => p<{ projecten: Project[] }>('projecten', { alles }).then((x) => x.projecten),
  project: (id: string) => p<{ project: Project; sprints: Sprint[]; leden: { id: string; rol: string; sinds: number }[] }>('project', { id }),
  projectOpslaan: (project: Partial<Project>) => p<{ project: Project }>('project-opslaan', { project }).then((x) => x.project),
  projectArchiveren: (id: string, archief: boolean) => p<{ project: Project }>('project-archiveren', { id, archief }).then((x) => x.project),
  ledenZetten: (projectId: string, leden: { id: string; rol: string }[]) => p('leden-zetten', { projectId, leden }),

  taken: (filter: { projectId?: string; mijn?: boolean; open?: boolean }) =>
    p<{ taken: Taak[] }>('taken', filter).then((x) => x.taken),
  taak: (id: string) => p<{ taak: Taak; reacties: Reactie[]; subtaken: Taak[] }>('taak', { id }),
  taakOpslaan: (taak: Partial<Taak>) => p<{ taak: Taak }>('taak-opslaan', { taak }).then((x) => x.taak),
  takenAanmaken: (taken: Partial<Taak>[]) => p<{ taken: Taak[] }>('taken-aanmaken', { taken }).then((x) => x.taken),
  takenVerplaatsen: (zetten: { id: string; status: string; volgorde: number }[]) => p('taken-verplaatsen', { zetten }),
  taakVerwijderen: (id: string) => p('taak-verwijderen', { id }),
  reactie: (taakId: string, tekst: string) => p<{ reactie: Reactie }>('reactie', { taakId, tekst }).then((x) => x.reactie),
  zoeken: (q: string) => p<{ taken: Taak[]; projecten: Project[] }>('zoeken', { q }),

  sprintOpslaan: (sprint: Partial<Sprint>) => p<{ sprint: Sprint }>('sprint-opslaan', { sprint }).then((x) => x.sprint),
  sprintStarten: (id: string) => p<{ sprint: Sprint }>('sprint-starten', { id }).then((x) => x.sprint),
  sprintAfronden: (id: string, naar: string | null) => p<{ sprint: Sprint; klaar: number; open: number }>('sprint-afronden', { id, naar }),
  sprintVerwijderen: (id: string) => p('sprint-verwijderen', { id }),

  borden: (projectId?: string) => p<{ borden: BordMeta[] }>('borden', { projectId }).then((x) => x.borden),
  bord: (id: string) => p<{ bord: BordMeta; items: Record<string, Onderdeel> }>('bord', { id }),
  bordNieuw: (naam: string, projectId: string | null) => p<{ bord: BordMeta }>('bord-nieuw', { naam, projectId }).then((x) => x.bord),
  bordVersie: (id: string) => p<{ versie: number; door: string | null; doorNaam: string | null; at: number }>('bord-versie', { id }),
  bordOpslaan: (id: string, zet: Record<string, Onderdeel>, weg: string[]) =>
    p<{ versie: number; aantal: number }>('bord-opslaan', { id, zet, weg }),
  bordHernoemen: (id: string, naam: string) => p<{ bord: BordMeta }>('bord-hernoemen', { id, naam }).then((x) => x.bord),
  bordArchiveren: (id: string) => p('bord-archiveren', { id }),

  activiteit: (filter: { projectId?: string; taakId?: string; limiet?: number }) =>
    p<{ activiteit: Activiteit[]; bereikbaar: boolean }>('activiteit', filter),

  aiStart: (soort: AiSoort, lijf: Record<string, unknown>) =>
    p<{ opdracht: string; gesprekId?: string; online?: boolean }>('ai-start', { ...lijf, soort }),
  aiStand: (opdracht: string) => p<AiStand>('ai-stand', { opdracht }),
  aiOnline: () => p<{ online: boolean; laatst: number | null; model: string }>('ai-online'),
  aiGesprekken: (projectId?: string) => p<{ gesprekken: Gesprek[] }>('ai-gesprekken', { projectId }).then((x) => x.gesprekken),
  aiGesprek: (id: string) => p<{ gesprek: Gesprek }>('ai-gesprek', { id }).then((x) => x.gesprek),
  aiGesprekWeg: (id: string) => p('ai-gesprek-weg', { id }),
}

/* ------------------------------------------------------------------ *
 *  GitHub
 * ------------------------------------------------------------------ */

const r = (repo: string) => `repo/${encodeURIComponent(repo)}`
const qs = (w: Record<string, string | number | undefined | null>) => {
  const s = new URLSearchParams()
  for (const [k, v] of Object.entries(w)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v))
  const t = s.toString()
  return t ? '?' + t : ''
}

export const github = {
  stand: () => gh<{ stand: GithubStand }>('stand').then((x) => x.stand),
  repos: () => gh<{ repos: Repo[] }>('repos').then((x) => x.repos),
  repoAanmaken: (lijf: { naam: string; omschrijving?: string; openbaar?: boolean; leesmij?: boolean; gitignore?: string; licentie?: string }) =>
    gh<{ repo: Repo }>('repos', { methode: 'POST', lijf }).then((x) => x.repo),
  changelog: (dagen = 14) => gh<{ commits: Commit[]; repos: string[] }>('changelog' + qs({ dagen })),
  zoekCommits: (q: string) => gh<{ commits: Commit[] }>('zoek-commits' + qs({ q })).then((x) => x.commits),

  repo: (repo: string) => gh<RepoDetail>(r(repo)),
  commits: (repo: string, o: { tak?: string; pagina?: number; pad?: string } = {}) =>
    gh<{ commits: Commit[] }>(`${r(repo)}/commits${qs(o)}`).then((x) => x.commits),
  commit: (repo: string, sha: string) => gh<{ commit: Commit }>(`${r(repo)}/commit/${sha}`).then((x) => x.commit),
  takken: (repo: string) => gh<{ takken: Tak[] }>(`${r(repo)}/takken`).then((x) => x.takken),
  pulls: (repo: string, staat = 'open') => gh<{ pulls: PullRequest[] }>(`${r(repo)}/pulls${qs({ staat })}`).then((x) => x.pulls),
  pull: (repo: string, n: number) => gh<{ pull: PullRequest; bestanden: CommitBestand[]; commits: Commit[] }>(`${r(repo)}/pull/${n}`),
  issues: (repo: string, staat = 'open') => gh<{ issues: Issue[] }>(`${r(repo)}/issues${qs({ staat })}`).then((x) => x.issues),
  issueAanmaken: (repo: string, lijf: { titel: string; tekst: string; labels?: string[] }) =>
    gh<{ issue: Issue }>(`${r(repo)}/issues`, { methode: 'POST', lijf }).then((x) => x.issue),
  releases: (repo: string) => gh<{ releases: Release[] }>(`${r(repo)}/releases`).then((x) => x.releases),
  map: (repo: string, pad: string, ref?: string) =>
    gh<{ map?: Bestand[]; bestand?: BestandInhoud }>(`${r(repo)}/bestanden${qs({ pad, ref })}`),
  acties: (repo: string) => gh<{ runs: Workflowrun[] }>(`${r(repo)}/acties`).then((x) => x.runs),
  bijdragers: (repo: string) => gh<{ bijdragers: Bijdrager[] }>(`${r(repo)}/bijdragers`).then((x) => x.bijdragers),
  vergelijk: (repo: string, van: string, naar: string) =>
    gh<{ voor: number; achter: number; commits: Commit[] }>(`${r(repo)}/vergelijk${qs({ van, naar })}`),
}

/** De reponaam uit "eigenaar/naam": de Worker kent de eigenaar zelf. */
export function repoUit(volledig: string | null | undefined): string | null {
  if (!volledig) return null
  const delen = volledig.split('/')
  return delen[1] ?? delen[0] ?? null
}
